import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { Client } from '../shared/model/client.model';
import { DefaultDataService } from './default-data.service';
import { WebAPIResult } from './employee.service';
import { EventService } from './event.service';

@Injectable({
  providedIn: 'root',
})
export class MarketingSettingsService {
  url = '/api/BackOffice/MarketingSettings';

  constructor(private http: HttpClient, private defaultData: DefaultDataService, private events: EventService) {}

  public getMarketingSettings(): Observable<MarketingSettingsData> {
    return this.http.get<MarketingSettingsData>(this.url);
  }

  public saveMarketingSettings(marketingSettingsData: MarketingSettingsData): Observable<WebAPIResult> {
    return this.http.post<WebAPIResult>(this.url, marketingSettingsData).pipe(
      take(1),
      catchError((e) => {
        const d: WebAPIResult = e as WebAPIResult;
        return of(d);
      }),
      tap((r) => {
        this.defaultData.defData.marketingSettings = marketingSettingsData;
        this.events.DefaultDataChanged(this.defaultData.defData);

        const d: WebAPIResult = r as WebAPIResult;
        return d;
      })
    );
  }

  public sendEmail(req: EmailRequest): Observable<WebAPIResult> {
    return this.http.post<WebAPIResult>(this.url + '/SendEmail', req).pipe(
      take(1),
      catchError((e) => {
        const d: WebAPIResult = e as WebAPIResult;
        return of(d);
      }),
      tap((r) => {
        const d: WebAPIResult = r as WebAPIResult;
        return d;
      })
    );
  }

  public sendSMS(req: SMSRequest): Observable<WebAPIResult> {
    return this.http.post<WebAPIResult>(this.url + '/SendSMS', req).pipe(
      take(1),
      catchError((e) => {
        const d: WebAPIResult = e as WebAPIResult;
        return of(d);
      }),
      tap((r) => {
        const d: WebAPIResult = r as WebAPIResult;
        return d;
      })
    );
  }

  public getEmailCredentialsAreValid(marketingSettingsData: MarketingSettingsData): boolean {
    return !!(
      marketingSettingsData?.EmailMarketingFromEmailAddress &&
      marketingSettingsData?.EmailMarketingPassword &&
      marketingSettingsData?.EmailMarketingPort &&
      marketingSettingsData?.EmailMarketingServerName &&
      marketingSettingsData?.EmailMarketingUsername
    );
  }

  public getSMSCredentialsAreValid(marketingSettingsData: MarketingSettingsData): boolean {
    return !!(marketingSettingsData?.SMSMarketingApiKey && marketingSettingsData?.SMSMarketingSenderName);
  }
}

export class MarketingAuditLog {
  Id: number;
  Client: number;
  ClientName: string;
  ClientNavigation: Client;
  Message: string;
  MessageSubject: string;
  SentDatetime: Date;
  SendMethod: string;
  SendResult: string;
  SentTo: string;
  Success: boolean;
}

export class EmailRequest {
  Body: string;
  ClientId: number;
  FromEmailAddress: string;
  ReplyToEmailAddress: string;
  Password: string;
  Port?: number;
  SenderName: string;
  ServerName: string;
  Subject: string;
  ToEmailAddress: string;
  Username: string;
  UseEncryption: boolean;
  UseTestTemplate: boolean;
  UseUnsubscribeWrapper: boolean;

  constructor(marketingSettingsData?: MarketingSettingsData, toEmailAddress?: string, toClient?: number) {
    this.ClientId = toClient || null;
    this.FromEmailAddress = marketingSettingsData?.EmailMarketingFromEmailAddress;
    this.ReplyToEmailAddress = marketingSettingsData?.EmailMarketingReplyToEmailAddress;
    this.Password = marketingSettingsData?.EmailMarketingPassword;
    this.Port = marketingSettingsData?.EmailMarketingPort;
    this.SenderName = marketingSettingsData?.EmailMarketingSenderName;
    this.ServerName = marketingSettingsData?.EmailMarketingServerName;
    this.ToEmailAddress = toEmailAddress || '';
    this.Username = marketingSettingsData?.EmailMarketingUsername;
    this.UseEncryption = marketingSettingsData?.EmailMarketingUseEncryption;
  }
}

export class SMSRequest {
  ApiKey: string;
  ClientId: number;
  CreditsRemaining: number;
  Message: string;
  ReceiverNumber: string;
  SenderName: string;

  constructor(marketingSettingsData?: MarketingSettingsData, receiverNumber?: string, toClient?: number) {
    this.ApiKey = marketingSettingsData?.SMSMarketingApiKey || '';
    this.ClientId = toClient || null;
    this.CreditsRemaining = marketingSettingsData?.SMSMarketingCreditsRemaining;
    this.ReceiverNumber = receiverNumber || '';
    this.SenderName = marketingSettingsData?.SMSMarketingSenderName;
  }
}

export class MarketingSettingsData {
  AutoMessage1DayEmail: string = '';
  AutoMessage1DayEmailSubject: string = '';
  AutoMessage1DaySMS: string = '';
  AutoMessage1DaySendEmail: boolean = false;
  AutoMessage1DaySendSMS: boolean = false;

  AutoMessage7DayEmail: string = '';
  AutoMessage7DayEmailSubject: string = '';
  AutoMessage7DaySMS: string = '';
  AutoMessage7DaySendEmail: boolean = false;
  AutoMessage7DaySendSMS: boolean = false;

  AutoMessage28DayEmail: string = '';
  AutoMessage28DayEmailSubject: string = '';
  AutoMessage28DaySMS: string = '';
  AutoMessage28DaySendEmail: boolean = false;
  AutoMessage28DaySendSMS: boolean = false;

  AutoMessage90DayEmail: string = '';
  AutoMessage90DayEmailSubject: string = '';
  AutoMessage90DaySMS: string = '';
  AutoMessage90DaySendEmail: boolean = false;
  AutoMessage90DaySendSMS: boolean = false;

  AutoMessage180DayEmail: string = '';
  AutoMessage180DayEmailSubject: string = '';
  AutoMessage180DaySMS: string = '';
  AutoMessage180DaySendEmail: boolean = false;
  AutoMessage180DaySendSMS: boolean = false;

  AutoMessage365DayEmail: string = '';
  AutoMessage365DayEmailSubject: string = '';
  AutoMessage365DaySMS: string = '';
  AutoMessage365DaySendEmail: boolean = false;
  AutoMessage365DaySendSMS: boolean = false;

  AutoMessageAppConfirmationEmail: string = '';
  AutoMessageAppConfirmationEmailSubject: string = '';
  AutoMessageAppConfirmationSMS: string = '';
  AutoMessageAppConfirmationSendEmail: boolean = false;
  AutoMessageAppConfirmationSendSMS: boolean = false;

  AutoMessageAppReminderEmail: string = '';
  AutoMessageAppReminderEmailSubject: string = '';
  AutoMessageAppReminderSMS: string = '';
  AutoMessageAppReminderSendEmail: boolean = false;
  AutoMessageAppReminderSendSMS: boolean = false;

  AutoMessageBirthdayEmail: string = '';
  AutoMessageBirthdayEmailSubject: string = '';
  AutoMessageBirthdaySMS: string = '';
  AutoMessageBirthdaySendEmail: boolean = false;
  AutoMessageBirthdaySendSMS: boolean = false;

  AutoMessageSignUpEmail: string = '';
  AutoMessageSignUpEmailSubject: string = '';
  AutoMessageSignUpSMS: string = '';
  AutoMessageSignUpSendEmail: boolean = false;
  AutoMessageSignUpSendSMS: boolean = false;

  AutoMessageXSpendAmount: number;
  AutoMessageXSpendEmail: string = '';
  AutoMessageXSpendEmailSubject: string = '';
  AutoMessageXSpendSMS: string = '';
  AutoMessageXSpendSendEmail: boolean = false;
  AutoMessageXSpendSendSMS: boolean = false;

  AutoMessageXAppointmentAmount: number;
  AutoMessageXAppointmentEmail: string = '';
  AutoMessageXAppointmentEmailSubject: string = '';
  AutoMessageXAppointmentSMS: string = '';
  AutoMessageXAppointmentSendEmail: boolean = false;
  AutoMessageXAppointmentSendSMS: boolean = false;

  EmailMarketingUseEncryption: boolean = true;
  EmailMarketingFromEmailAddress: string = '';
  EmailMarketingReplyToEmailAddress: string = '';
  EmailMarketingPort?: number;
  EmailMarketingSenderName: string = '';
  EmailMarketingServerName: string = '';
  EmailMarketingPassword: string = '';
  EmailMarketingUsername: string = '';

  SMSMarketingApiKey: string = '';
  SMSMarketingCreditsRemaining?: number;
  SMSMarketingSenderName: string = '';
}

export enum AutomatedMessagesTabName {
  AutoMessage1Day = '1Day',
  AutoMessage7Day = '7Day',
  AutoMessage28Day = '28Day',
  AutoMessage90Day = '90Day',
  AutoMessage180Day = '180Day',
  AutoMessage365Day = '365Day',
  AutoMessageAppConfirmation = 'AppConfirmation',
  AutoMessageAppReminder = 'AppReminder',
  AutoMessageBirthday = 'Birthday',
  AutoMessageSignUp = 'SignUp',
  AutoMessageXSpend = 'XSpend',
  AutoMessageXAppointment = 'XAppointment',
}

export class AutomatedMessagesTab {
  Id: number;
  Context: string;
  Name: string;
  FormData: AutomatedMessagesForm;
  FormInstance?: any;
  MailMergeFields: string[];
  TestEmailForm: EmailRequest;
  TestSMSForm: SMSRequest;
  Title: string;
}

export class AutomatedMessagesForm {
  Email: string = '';
  EmailSubject: string = '';
  SMS: string = '';
  SendEmail: boolean = false;
  SendSMS: boolean = false;
  XAmount?: number;

  constructor(marketingSettingsData?: MarketingSettingsData, tabName?: string) {
    if (!marketingSettingsData || !tabName) {
      this.Email = '';
      this.EmailSubject = '';
      this.SMS = '';
      this.SendEmail = false;
      this.SendSMS = false;
    } else {
      switch (tabName) {
        case AutomatedMessagesTabName.AutoMessage1Day:
          this.Email = marketingSettingsData.AutoMessage1DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage1DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage1DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage1DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage1DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessage7Day:
          this.Email = marketingSettingsData.AutoMessage7DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage7DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage7DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage7DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage7DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessage28Day:
          this.Email = marketingSettingsData.AutoMessage28DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage28DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage28DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage28DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage28DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessage90Day:
          this.Email = marketingSettingsData.AutoMessage90DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage90DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage90DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage90DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage90DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessage180Day:
          this.Email = marketingSettingsData.AutoMessage180DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage180DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage180DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage180DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage180DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessage365Day:
          this.Email = marketingSettingsData.AutoMessage365DayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessage365DayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessage365DaySMS;
          this.SendEmail = marketingSettingsData.AutoMessage365DaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessage365DaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessageAppConfirmation:
          this.Email = marketingSettingsData.AutoMessageAppConfirmationEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageAppConfirmationEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageAppConfirmationSMS;
          this.SendEmail = marketingSettingsData.AutoMessageAppConfirmationSendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageAppConfirmationSendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessageAppReminder:
          this.Email = marketingSettingsData.AutoMessageAppReminderEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageAppReminderEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageAppReminderSMS;
          this.SendEmail = marketingSettingsData.AutoMessageAppReminderSendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageAppReminderSendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessageBirthday:
          this.Email = marketingSettingsData.AutoMessageBirthdayEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageBirthdayEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageBirthdaySMS;
          this.SendEmail = marketingSettingsData.AutoMessageBirthdaySendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageBirthdaySendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessageSignUp:
          this.Email = marketingSettingsData.AutoMessageSignUpEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageSignUpEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageSignUpSMS;
          this.SendEmail = marketingSettingsData.AutoMessageSignUpSendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageSignUpSendSMS;
          break;
        case AutomatedMessagesTabName.AutoMessageXSpend:
          this.Email = marketingSettingsData.AutoMessageXSpendEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageXSpendEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageXSpendSMS;
          this.SendEmail = marketingSettingsData.AutoMessageXSpendSendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageXSpendSendSMS;
          this.XAmount = marketingSettingsData.AutoMessageXSpendAmount;
          break;
        case AutomatedMessagesTabName.AutoMessageXAppointment:
          this.Email = marketingSettingsData.AutoMessageXAppointmentEmail;
          this.EmailSubject = marketingSettingsData.AutoMessageXAppointmentEmailSubject;
          this.SMS = marketingSettingsData.AutoMessageXAppointmentSMS;
          this.SendEmail = marketingSettingsData.AutoMessageXAppointmentSendEmail;
          this.SendSMS = marketingSettingsData.AutoMessageXAppointmentSendSMS;
          this.XAmount = marketingSettingsData.AutoMessageXAppointmentAmount;
          break;
        default:
          break;
      }
    }
  }
}
